import { useMutation } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { Box, Grid, Stack } from "@mui/system";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { FormTextField } from "components/Forms/FormTextField";
import { StatusChip } from "components/StatusChip";
import { TagPicker } from "components/TagPicker";
import { ValueWithStaffEditedNotation } from "components/ValueWithStaffEditedNotation";
import { TagType, WasteManifestSource } from "generated-graphql/graphql";
import { transform } from "hooks/transform/transformWasteLineUnified";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useWasteLineUnifiedSuspense } from "hooks/useWasteLineUnified";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import {
  formatAmountSourceValues,
  largestAmountText,
} from "routes/Customer/Waste/Manifests/AmountCell";
import { FieldSourceDot } from "routes/Customer/Waste/Manifests/FieldSourceDot";
import { SOURCE_LABEL } from "routes/Customer/Waste/Manifests/manifestSourceDisplay";
import { SourcesDisagreeTooltip } from "routes/Customer/Waste/Manifests/SourcesDisagreeTooltip";
import {
  FieldSourceValues,
  useManifestDiscrepancyFields,
} from "routes/Customer/Waste/Manifests/useManifestDiscrepancyFields";
import { hasCriticalIssues } from "util/forms";

import { EDIT_WASTE_LINE_UNIFIED_MUTATION, WASTE_LINES_UNIFIED_QUERY } from "./api";
import { DeleteLineItemDialog } from "./DeleteLineItemDialog";
import {
  useWasteLineUnifiedInputValidation,
  WasteLineUnifiedFormData,
} from "./validationHooks/useWasteLineUnifiedInputValidation";

interface LineItemDetailDialogProps {
  processedManifestId: string | undefined;
  lineItemId: string;
  onClose: () => void;
}

type FieldSource = { field: string; source: WasteManifestSource };

/** Compact fields lay out 3-up on wide modals, 2-up on small, 1-up on phones. */
const COMPACT_SIZE = { xs: 12, sm: 6, md: 4 } as const;

/**
 * A read-only field that mirrors the manifest-data boxes: the displayed value
 * is the line's resolved (primary → fallback) value, and a color-by-source dot
 * sits beside the label only when that value came from a non-primary source.
 * A warning icon flags an open cross-source disagreement.
 */
function LineItemField({
  label,
  value,
  fieldKey,
  primarySource,
  fieldSources,
  disagreements,
  lineValues,
  disagreementTooltip,
  wrap,
  renderValue,
}: {
  label: string;
  value?: string | null;
  /** Provenance key in `fieldSources` / disagreement set; omit for single-source fields. */
  fieldKey?: string;
  primarySource?: WasteManifestSource;
  fieldSources: readonly FieldSource[];
  disagreements?: Set<string>;
  /** Per-field source values for this line; builds the disagreement tooltip. */
  lineValues?: Map<string, FieldSourceValues>;
  /** Explicit disagreement tooltip; overrides the one derived from `lineValues`. */
  disagreementTooltip?: ReactNode;
  wrap?: boolean;
  renderValue?: (value: ReactNode) => ReactNode;
}) {
  const source = fieldKey ? fieldSources.find((fs) => fs.field === fieldKey)?.source : undefined;
  const isFallback = source != null && primarySource != null && source !== primarySource;
  const labelNode = isFallback ? (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      {label}
      <FieldSourceDot source={source} tooltip={`Data source: ${SOURCE_LABEL[source]}`} />
    </Box>
  ) : (
    label
  );
  const fieldValues = fieldKey ? lineValues?.get(fieldKey) : undefined;
  const tooltip =
    disagreementTooltip ??
    (fieldValues ? <SourcesDisagreeTooltip values={fieldValues} /> : undefined);
  return (
    <Box
      sx={(theme) => ({
        height: "100%",
        // Floor so short/empty fields (e.g. Waste Min Code, Consent #) match the
        // height of fields that carry a value; within-row stretch handles taller
        // wrapping fields.
        minHeight: 60,
        borderRadius: 1,
        px: 1.5,
        py: 1,
        // Light-gray control background so the fields read as boxes.
        bgcolor: theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[800],
      })}
    >
      <DisplayOnlyField
        label={labelNode}
        text={value ?? undefined}
        copyable={false}
        wrapValue={wrap}
        warningIcon={fieldKey ? (disagreements?.has(fieldKey) ?? false) : false}
        warningTooltip={tooltip}
        renderValue={renderValue}
      />
    </Box>
  );
}

/**
 * Single dialog for a manifest line item: a source-aware read-only view that
 * flips in place into the staff edit form (editable vendor fields, mark
 * not-reportable, delete) — replacing the old drawer control-swap + separate
 * edit dialog. Source-agnostic; values resolve to the line's primary source
 * with per-field dots for fallbacks.
 */
export function LineItemDetailDialog({
  processedManifestId,
  lineItemId,
  onClose,
}: LineItemDetailDialogProps) {
  const alerts = useAlerts();
  const { isStaff } = useCurrentUser();
  const { lineItem } = useWasteLineUnifiedSuspense(lineItemId);
  const { lineItemDisagreementFields, lineItemValuesByField } = useManifestDiscrepancyFields(
    processedManifestId,
    {
      skip: !isStaff,
    }
  );
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [edit, { loading }] = useMutation(EDIT_WASTE_LINE_UNIFIED_MUTATION);

  const defaultValues = useMemo<WasteLineUnifiedFormData>(
    () => ({
      willNotReport: lineItem?.willNotReport ?? false,
      lineItem: lineItem?.lineItem ?? null,
      dotDescription: lineItem?.vendorDotDescription ?? null,
      vendorAmount: lineItem?.vendorAmount ?? null,
      eManifestAmount: lineItem?.eManifestAmount ?? null,
      vendorProfileNumber: lineItem?.profileNumber ?? null,
      vendorManagementMethodCode: lineItem?.managementMethodCode ?? null,
      vendorFormCode: lineItem?.formCode ?? null,
      vendorSourceCode: lineItem?.sourceCode ?? null,
      tags: lineItem?.tags?.map((t) => ({ id: t.id, name: t.name })) ?? [],
    }),
    [lineItem]
  );

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    issues,
  } = useValidatingForm<WasteLineUnifiedFormData>(
    defaultValues,
    [],
    useWasteLineUnifiedInputValidation()
  );

  const handleEdit = useCallback(
    async (formData: WasteLineUnifiedFormData) => {
      if (!lineItem) return;
      try {
        const input = transform({
          willNotReport: formData.willNotReport,
          lineItem: formData.lineItem ?? "",
          dotDescription: formData.dotDescription ?? "",
          vendorAmount: formData.vendorAmount ?? 0,
          vendorProfileNumber: formData.vendorProfileNumber ?? "",
          vendorManagementMethodCode: formData.vendorManagementMethodCode ?? "",
          vendorFormCode: formData.vendorFormCode ?? "",
          vendorSourceCode: formData.vendorSourceCode ?? "",
          tags: formData.tags,
        });
        await edit({
          variables: { id: lineItem.id, input },
          refetchQueries: [WASTE_LINES_UNIFIED_QUERY, "WasteTags"],
        });
        alerts.success("Line item updated successfully!");
        setMode("view");
      } catch (err) {
        alerts.error(`There was an error editing the line item`, err);
      }
    },
    [edit, lineItem, alerts]
  );

  if (!lineItem) return null;

  const primarySource = lineItem.primarySource ?? undefined;
  const fieldSources: readonly FieldSource[] = lineItem.fieldSources ?? [];
  const disagreements = lineItemDisagreementFields.get(lineItem.id);
  const lineValues = lineItemValuesByField.get(lineItem.id);
  const canEdit = isStaff && lineItem.vendorAmount != null;
  const isEditing = mode === "edit";

  // Per-source amounts → the largest is shown; the three feed the disagree tooltip.
  const amounts = {
    [WasteManifestSource.EManifest]: lineItem.eManifestAmount,
    [WasteManifestSource.Vendor]: lineItem.vendorAmount,
    [WasteManifestSource.ManifestDocument]: lineItem.documentAmount,
  };
  const amountText = largestAmountText(amounts);

  const field = (props: {
    label: string;
    value?: string | null;
    fieldKey?: string;
    wrap?: boolean;
    disagreementTooltip?: ReactNode;
    renderValue?: (value: ReactNode) => ReactNode;
  }) => (
    <LineItemField
      {...props}
      primarySource={primarySource}
      fieldSources={fieldSources}
      disagreements={disagreements}
      lineValues={lineValues}
    />
  );

  const editedNotation = (value: ReactNode, isEdited: boolean) => (
    <ValueWithStaffEditedNotation value={value} isEdited={isEdited} />
  );

  // Header: bold "Line Item #N:" + the line name, an orange pencil affordance
  // (mirrors the manifest header), and the manifest # tucked just beneath.
  const titleNode = (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 0 }}>
          Line Item{lineItem.wasteLineNumber ? ` #${lineItem.wasteLineNumber}` : ""}
        </Typography>
        {canEdit && !isEditing && (
          <Tooltip title="Edit Line Item">
            <IconButton
              size="small"
              color="warning"
              onClick={() => setMode("edit")}
              aria-label="Edit Line Item"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      {(lineItem.manifestTrackingNumber || primarySource) && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          flexWrap="wrap"
          sx={{ mt: 0.25, color: "text.secondary" }}
        >
          {lineItem.manifestTrackingNumber && (
            <Typography variant="caption" color="inherit">
              Manifest #{lineItem.manifestTrackingNumber}
            </Typography>
          )}
          {lineItem.manifestTrackingNumber && primarySource && (
            <Box
              sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "text.disabled" }}
              aria-hidden
            />
          )}
          {primarySource && (
            <Typography variant="caption" color="inherit">
              Primary data source: {SOURCE_LABEL[primarySource]}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        fullWidth
        maxWidth="md"
        title={titleNode}
        contentSx={{ pb: 3 }}
        noPrimaryAction={!isEditing}
        hideCancelButton
        primaryActionButtonProps={
          isEditing
            ? {
                saveText: "Save",
                onClick: handleSubmit(handleEdit),
                loading,
                disabled: hasCriticalIssues(issues) || !isDirty,
              }
            : undefined
        }
        additionalPrimaryActionElements={
          isEditing
            ? [
                <Button key="cancel" disabled={loading} onClick={() => setMode("view")}>
                  Cancel
                </Button>,
              ]
            : []
        }
        secondaryActionElements={
          isEditing
            ? [
                <Button
                  key="delete"
                  color="error"
                  disabled={loading}
                  onClick={() => setShowDeleteDialog(true)}
                >
                  Delete Line Item
                </Button>,
              ]
            : []
        }
      >
        {isEditing ? (
          <form>
            {/* Tight row spacing: each FormTextField already reserves a helper-text
                line, so extra row gap makes the edit view feel far airier than the
                read-only one. */}
            <Grid container rowSpacing={0.5} columnSpacing={2}>
              <Grid size={12}>
                <CheckboxField
                  name="willNotReport"
                  label="Mark as not reportable"
                  control={control}
                />
              </Grid>
              <Grid size={12}>
                <FormTextField
                  name="lineItem"
                  label="Line Item Name"
                  control={control}
                  textFieldProps={{ multiline: true, rows: 2, size: "small" }}
                />
              </Grid>
              <Grid size={12}>
                <FormTextField
                  name="dotDescription"
                  label="DOT Description"
                  control={control}
                  textFieldProps={{ size: "small" }}
                />
              </Grid>
              <Grid size={COMPACT_SIZE}>
                <FormTextField
                  name="vendorAmount"
                  label="Amount (Vendor)"
                  type="number"
                  control={control}
                  textFieldProps={{ size: "small" }}
                />
              </Grid>
              <Grid size={COMPACT_SIZE}>
                <FormTextField
                  name="eManifestAmount"
                  label="Amount (e‑Manifest)"
                  control={control}
                  disabled
                  textFieldProps={{ size: "small" }}
                />
              </Grid>
              <Grid size={COMPACT_SIZE}>
                <FormTextField
                  name="vendorProfileNumber"
                  label="Profile Number"
                  control={control}
                  textFieldProps={{ size: "small" }}
                />
              </Grid>
              <Grid size={COMPACT_SIZE}>
                <FormTextField
                  name="vendorManagementMethodCode"
                  label="Management Method Code"
                  control={control}
                  textFieldProps={{ size: "small" }}
                />
              </Grid>
              <Grid size={COMPACT_SIZE}>
                <FormTextField
                  name="vendorFormCode"
                  label="Form Code"
                  control={control}
                  textFieldProps={{ size: "small" }}
                />
              </Grid>
              <Grid size={COMPACT_SIZE}>
                <FormTextField
                  name="vendorSourceCode"
                  label="Source Code"
                  control={control}
                  textFieldProps={{ size: "small" }}
                />
              </Grid>
              <Grid size={12}>
                <Controller
                  name="tags"
                  control={control}
                  render={({ field: tagField, fieldState }) => (
                    <TagPicker {...tagField} {...fieldState} tagType={TagType.Waste} label="Tags" />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        ) : (
          <Grid container spacing={2}>
            <Grid size={12}>
              {field({
                label: "Line Item",
                value: lineItem.lineItem,
                fieldKey: "lineItem",
                wrap: true,
                renderValue: (value) =>
                  editedNotation(
                    <>
                      <Box
                        component="span"
                        sx={{ fontWeight: 600, "& *": { fontWeight: "inherit" } }}
                      >
                        {value}
                      </Box>
                      {lineItem.willNotReport && (
                        <StatusChip
                          color="info"
                          variant="outlined"
                          label="Not Reportable"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </>,
                    !!lineItem.manuallyEdited &&
                      lineItem.originalVendorWasteLine?.vendorProfileDescription !==
                        lineItem.lineItem
                  ),
              })}
            </Grid>
            <Grid size={12}>
              {field({
                label: "DOT Description",
                value: lineItem.dotDescription,
                fieldKey: "dotDescription",
                wrap: true,
                renderValue: (value) =>
                  editedNotation(
                    value,
                    !!lineItem.manuallyEdited &&
                      lineItem.originalVendorWasteLine?.dotPrintedInformation !==
                        lineItem.vendorDotDescription &&
                      lineItem.originalVendorWasteLine?.nonHazWasteDescription !==
                        lineItem.vendorDotDescription
                  ),
              })}
            </Grid>
            <Grid size={COMPACT_SIZE}>
              {field({
                label: "Amount",
                value: amountText,
                fieldKey: "amount",
                disagreementTooltip: (
                  <SourcesDisagreeTooltip values={formatAmountSourceValues(amounts)} />
                ),
              })}
            </Grid>
            <Grid size={COMPACT_SIZE}>
              {field({
                label: "Profile #",
                value: lineItem.profileNumber,
                fieldKey: "profileNumber",
                renderValue: (value) =>
                  editedNotation(
                    value,
                    !!lineItem.manuallyEdited &&
                      lineItem.originalVendorWasteLine?.vendorProfileNumber !==
                        lineItem.profileNumber
                  ),
              })}
            </Grid>
            <Grid size={COMPACT_SIZE}>
              {field({
                label: "Management Method Code",
                value: lineItem.managementMethodCode,
                fieldKey: "managementMethodCode",
                renderValue: (value) =>
                  editedNotation(
                    value,
                    !!lineItem.manuallyEdited &&
                      lineItem.originalVendorWasteLine?.managementMethodCode !==
                        lineItem.managementMethodCode
                  ),
              })}
            </Grid>
            {lineItem.managementMethodDescription && (
              <Grid size={COMPACT_SIZE}>
                {field({
                  label: "Management Method",
                  value: lineItem.managementMethodDescription,
                  wrap: true,
                })}
              </Grid>
            )}
            <Grid size={COMPACT_SIZE}>
              {field({
                label: "Form Code",
                value: lineItem.formCode,
                fieldKey: "formCode",
                renderValue: (value) =>
                  editedNotation(
                    value,
                    !!lineItem.manuallyEdited &&
                      lineItem.originalVendorFormCode !== lineItem.formCode
                  ),
              })}
            </Grid>
            {lineItem.formCodeDescription && (
              <Grid size={COMPACT_SIZE}>
                {field({ label: "Form", value: lineItem.formCodeDescription, wrap: true })}
              </Grid>
            )}
            <Grid size={COMPACT_SIZE}>
              {field({
                label: "Source Code",
                value: lineItem.sourceCode,
                fieldKey: "sourceCode",
                renderValue: (value) =>
                  editedNotation(
                    value,
                    !!lineItem.manuallyEdited &&
                      lineItem.originalVendorSourceCode !== lineItem.sourceCode
                  ),
              })}
            </Grid>
            {lineItem.sourceCodeDescription && (
              <Grid size={COMPACT_SIZE}>
                {field({ label: "Source", value: lineItem.sourceCodeDescription, wrap: true })}
              </Grid>
            )}
            <Grid size={COMPACT_SIZE}>
              {field({
                label: "Federal Waste Codes",
                value: lineItem.federalWasteCodes?.join(", "),
                fieldKey: "federalWasteCodes",
                wrap: true,
              })}
            </Grid>
            <Grid size={COMPACT_SIZE}>
              {field({
                label: "State Waste Codes",
                value: lineItem.stateWasteCodes?.join(", "),
                fieldKey: "stateWasteCodes",
                wrap: true,
              })}
            </Grid>
            <Grid size={COMPACT_SIZE}>
              {field({
                label: "DOT ID Number",
                value: lineItem.dotIdNumberDescription,
                wrap: true,
              })}
            </Grid>
            <Grid size={COMPACT_SIZE}>
              {field({ label: "Waste Minimization Code", value: lineItem.wasteMinimizationCode })}
            </Grid>
            <Grid size={COMPACT_SIZE}>
              {field({ label: "Consent Number", value: lineItem.consentNumber })}
            </Grid>
            {lineItem.dotHazardous != null && (
              <Grid size={COMPACT_SIZE}>
                {field({ label: "DOT Hazardous", value: lineItem.dotHazardous ? "Yes" : "No" })}
              </Grid>
            )}
            {lineItem.epaWaste != null && (
              <Grid size={COMPACT_SIZE}>
                {field({ label: "EPA Waste", value: lineItem.epaWaste ? "Yes" : "No" })}
              </Grid>
            )}
            {lineItem.wasteResidue != null && (
              <Grid size={COMPACT_SIZE}>
                {field({ label: "Waste Residue", value: lineItem.wasteResidue ? "Yes" : "No" })}
              </Grid>
            )}
            {lineItem.comments && (
              <Grid size={12}>
                {field({ label: "Comments", value: lineItem.comments, wrap: true })}
              </Grid>
            )}
            <Grid size={12}>
              {field({
                label: "Tags",
                renderValue: () =>
                  editedNotation(
                    lineItem.tags && lineItem.tags.length > 0 ? (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mt: 0.25 }}
                      >
                        {lineItem.tags.map((t) => (
                          <Chip key={t.id} label={t.name} size="small" />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        —
                      </Typography>
                    ),
                    !!lineItem.manuallyEdited &&
                      lineItem.originalVendorWasteLine?.tags
                        ?.map((t) => t.name)
                        .sort()
                        .join() !==
                        lineItem.tags
                          .map((t) => t.name)
                          .sort()
                          .join()
                  ),
              })}
            </Grid>
          </Grid>
        )}
      </Dialog>

      {showDeleteDialog && (
        <DeleteLineItemDialog
          lineItem={lineItem}
          onClose={() => setShowDeleteDialog(false)}
          onDeleteConfirm={() => {
            setShowDeleteDialog(false);
            onClose();
          }}
        />
      )}
    </>
  );
}
